// src/components/frontpage/UtilityCategoryList.jsx
// Category-based utility contact list. Each category row shows icon + translated name + count;
// clicking reveals that category's providers. Each provider is a leaf row that opens its own
// detail through the shared PresencePanel (matching Directory entries and Official Contacts).
// Two render modes (drillStyle):
//   - 'popout' (default): providers in an absolute pop-out panel, closes on outside-click + ESC.
//   - 'accordion': providers rendered inline below the row (position:static), no outside-click
//     dismiss — toggles purely on row click (matches the DirectoryNav accordion).
// Only one category open at a time; only one provider card open at a time (shared openListingId).

import React, { useRef, useEffect } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import PresencePanel from '../presence/PresencePanel';
import { listingToPresence } from '../presence/presenceSource';
import { SectionTitle, DirectoryRow, AccordionBranch, EntryBullet, DIR_FONT, OpenEntryFrame, AreaLabel } from './DirectorySection';
import { tokens } from '../../styles/designTokens';
import { useExpandInPlace } from '../../hooks/useExpandInPlace';

const UtilityCategoryList = ({ categories = [], compact = false, drillStyle = 'popout', openCategoryKey, onToggleCategory, openListingId, onToggleListing, onSuggestCorrection, isOpen = true, onToggleSection = null }) => {
  const { t } = useTranslation('frontpage');
  const popoutRef = useRef(null);
  const useAccordion = drillStyle === 'accordion';

  // The open category panel is this section's single-open unit, coordinated at the CATEGORY level
  // across every section. Controlled by the parent (DirectoryColumn owns one namespaced open-
  // category key, so opening a utility category closes an open directory category and clears any
  // open card) or local state in the SidebarLocality popout. The 'utility:' namespace keeps the
  // shared key distinct from the directory section's 'directory:' keys, even if a slug matched.
  // See src/hooks/useExpandInPlace.js.
  const CAT_NS = 'utility:';
  const categoryGroup = useExpandInPlace({ openId: openCategoryKey, onToggle: onToggleCategory });
  const openCategoryId = typeof categoryGroup.openId === 'string' && categoryGroup.openId.startsWith(CAT_NS)
    ? categoryGroup.openId.slice(CAT_NS.length)
    : null;

  // Open provider card: shared openListingId across all sections when controlled (DirectoryColumn
  // owns it — opening a provider closes any other open card AND preserves this category via
  // handleToggleListing), else local state in the SidebarLocality popout. A provider lives inside
  // its category, so opening one never touches the open category.
  const listingGroup = useExpandInPlace({ openId: openListingId, onToggle: onToggleListing });
  const openEntryId = listingGroup.openId;
  const toggleListing = listingGroup.toggle;

  const handleToggle = (catId) => {
    categoryGroup.toggle(CAT_NS + catId);
    if (!categoryGroup.controlled) toggleListing(null);
  };
  const closeCategory = () => {
    categoryGroup.toggle(null);
    if (!categoryGroup.controlled) toggleListing(null);
  };

  // Outside-click dismiss is a pop-out affordance only — skipped in accordion mode so a click
  // on another category header switches instead of collapsing. Escape closes in both modes.
  useEffect(() => {
    if (!openCategoryId) return;

    const handleKeyDown = (e) => {
      if (e.code === 'Escape') closeCategory();
    };
    document.addEventListener('keydown', handleKeyDown);

    let handleMouseDown;
    if (!useAccordion) {
      handleMouseDown = (e) => {
        if (popoutRef.current && !popoutRef.current.contains(e.target)) {
          closeCategory();
        }
      };
      document.addEventListener('mousedown', handleMouseDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (handleMouseDown) document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [openCategoryId, useAccordion]); // eslint-disable-line react-hooks/exhaustive-deps

  // One provider → its leaf row + inline PresencePanel detail. Utility providers carry no
  // group-link data, so they never bold. `languages` is the provider's spoken-languages string;
  // it maps to the presence source's description slot (mirrors contactToPresence).
  const renderProvider = (provider) => (
    <div
      key={provider.id}
      onClick={() => toggleListing(provider.id)}
      style={{
        padding: '0 4px',
        borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
        fontFamily: DIR_FONT
      }}
    >
      {/* This single wrapper serves BOTH drill styles (accordion branch and popout list) — the
          provider's detail render is not duplicated per style. Do NOT add a border to the
          category-level popout panel below (the one holding every provider in the open
          category) — that would frame a category, which the ruling forbids.
          Border-encloses-name correction, 16 Aug: OpenEntryFrame wraps the row AND its detail
          together (fixing both drill styles at once, since this function serves both), so the
          open-entry border frames the name, not just the detail beneath it. */}
      <OpenEntryFrame isOpen={openEntryId === provider.id}>
        <div style={{
          minHeight: compact ? '22px' : '26px', display: 'flex', alignItems: 'flex-start', gap: '8px',
          padding: '2px 0'
        }}>
          <EntryBullet />
          <span style={{ fontWeight: '400', fontSize: '13px', lineHeight: '1.35', color: '#1f2937' }}>
            {provider.name}
            <AreaLabel row={provider} />
          </span>
        </div>
        {openEntryId === provider.id && (
          <div
            onClick={(ev) => ev.stopPropagation()}
            style={{ marginTop: '4px' }}
          >
            <PresencePanel
              source={listingToPresence({ ...provider, description: provider.description || provider.languages })}
              density="compact"
              onSuggestCorrection={onSuggestCorrection ? () => onSuggestCorrection(provider) : undefined}
              onClose={() => toggleListing(null)}
            />
          </div>
        )}
      </OpenEntryFrame>
    </div>
  );

  return (
    <div style={{ marginBottom: '8px', position: 'relative' }}>
      {/* Section title (shared primitive — theme blue, one definition). onClick makes it the
          concertina's toggle (Directory column shell brief, Scope 3); omitted, it's the original
          non-interactive heading. */}
      <SectionTitle title={t('utility_contacts')} onClick={onToggleSection} />

      {isOpen && (
      <>
      {/* Honest empty state — no fabricated providers, no dead blank either. */}
      {categories.length === 0 && (
        <div style={{ fontFamily: DIR_FONT, fontSize: '12px', fontStyle: 'italic', color: tokens.colors.disabledText }}>
          {t('no_utility_contacts')}
        </div>
      )}

      {/* Category rows */}
      {categories.map(cat => (
        <div key={cat.id} style={{ position: 'relative' }}>
          <DirectoryRow
            icon={cat.icon}
            label={t(cat.translationKey)}
            count={cat.providers.length}
            isOpen={openCategoryId === cat.id}
            onClick={() => handleToggle(cat.id)}
            compact={compact}
          />

          {/* Providers — inline accordion branch or absolute pop-out per drillStyle */}
          {openCategoryId === cat.id && (
            useAccordion ? (
              <AccordionBranch>
                {cat.providers.map(renderProvider)}
              </AccordionBranch>
            ) : (
              <div
                ref={popoutRef}
                style={{
                  position: 'absolute',
                  left: '8px',
                  top: '100%',
                  zIndex: 200,
                  background: 'white',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  width: '240px',
                  fontFamily: DIR_FONT
                }}
              >
                {/* Category name — shown only in the pop-out (the row already labels it in accordion) */}
                <div style={{
                  fontWeight: '700',
                  fontSize: '13px',
                  color: '#1f2937',
                  marginBottom: '8px'
                }}>
                  {t(cat.translationKey)}
                </div>
                {cat.providers.map(renderProvider)}
              </div>
            )
          )}
        </div>
      ))}
      </>
      )}
    </div>
  );
};

export default UtilityCategoryList;
